import { ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import React from 'react';
import ReactDOM, { render } from 'react-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Router } from 'react-router-dom';

import { ApolloProvider } from '@sbu/contexts.apollo';
import { ToastsProvider } from '@sbu/contexts.toastscse';
import { useCybertronTheme } from '@sbu/hooks.cybertrontheme';

import { LoaderProvider } from 'shared/components/elements/Loader';
import { ErrorBoundary } from 'shared/components/utils/ErrorBoundary';
import { HotkeyDialog } from 'shared/components/utils/Hotkey';
import { Responsive } from 'shared/components/utils/Responsive';
import { Theme } from 'shared/components/utils/Theme';

import { Routes } from './core/routing';
import { getLegacyCybertronTheme } from './core/themes/cybertron';

import 'assets/css/reboot.scss';
import 'assets/css/styles.scss';
import 'assets/css/app.scss';
import '@sbu/fonts.labgrotesque';

import { KansoProvider, useKanso } from '@sbu/contexts.kanso';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';

const SENTRY_DSN =
  'https://8147eecfc949437f966bd46d7fe8c9b4@o236854.ingest.sentry.io/1404152';

// If production, initialize Sentry before the root React component is mounted.
if (process.env.NODE_ENV === 'production') {
  const options: Sentry.BrowserOptions = { dsn: SENTRY_DSN };

  const version = process.env.VERSION;
  if (version) {
    options.release = `admiral-ui@${version}`;
  }

  Sentry.init(options);
} else if (process.env.AXE_CORE === 'true') {
  const options = {
    resultTypes: ['violations'],
    runOnly: ['wcag2a', 'wcag2aa'],
  };
  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000, options);
}

const prepare = () => {
  // When running integration tests, MSW should be started by cypress
  if (
    process.env.ENABLE_MOCKS === 'true' &&
    !(process.env.INTEGRATION_TESTS === 'true')
  ) {
    const { worker } = require('./mocks/browser');
    return worker.start({
      onUnhandledRequest(req) {
        if (req.url.pathname.includes('/graphql')) {
          console.error(
            `Unhandled GraphQL request: ${req.method} ${req.url.href}`
          );
        }
      },
    });
  }
  return Promise.resolve();
};

const Themes = ({ children }) => {
  const { theme } = useCybertronTheme();
  const legacyCybertronTheme = getLegacyCybertronTheme(theme.palette);

  return (
    <ThemeProvider theme={theme}>
      <Theme reference={document.body} theme={legacyCybertronTheme}>
        {children}
      </Theme>
    </ThemeProvider>
  );
};

const Kanso = ({ children }) => {
  const kansoAppEnabled = useFeatureFlag(DynamicFeatureFlag.Kanso);
  const hasKansoEverBeenLaunched = localStorage?.getItem(
    'ui.hasKansoEverBeenLaunched'
  );
  const kansoModeEnabled =
    kansoAppEnabled && hasKansoEverBeenLaunched === 'true';

  return (
    <KansoProvider
      kansoModeEnabled={kansoModeEnabled}
      newRoutesEnabled={kansoModeEnabled}
    >
      {children}
    </KansoProvider>
  );
};

const RoutesContainer = () => {
  const { navigationEnabled } = useKanso();

  return navigationEnabled ? (
    <Routes />
  ) : (
    <HotkeyDialog>
      <Routes />
    </HotkeyDialog>
  );
};

const Root = () => {
  return (
    <HelmetProvider>
      <ApolloProvider>
        <Themes>
          <Router history={createBrowserHistory()}>
            <LoaderProvider>
              <Responsive>
                <Helmet
                  defaultTitle='Sumo Logic Cloud SIEM'
                  titleTemplate='%s | Sumo Logic Cloud SIEM'
                />
                <Kanso>
                  <ToastsProvider>
                    <ErrorBoundary
                      showChildren={true}
                      showMessage={false}
                      showNotification={true}
                    >
                      <RoutesContainer />
                    </ErrorBoundary>
                  </ToastsProvider>
                </Kanso>
              </Responsive>
            </LoaderProvider>
          </Router>
        </Themes>
      </ApolloProvider>
    </HelmetProvider>
  );
};

prepare().then(() => {
  render(<Root />, document.getElementById('root'));
});
